import React, { useState, useEffect, useCallback, useRef } from 'react';

import * as Alert from 'components/alert';
import * as Page from 'components/page';
import { RHP } from 'providers';
import { useApi } from 'hooks';

export function StartupStep9({ useRankingStartup, t }) {
    const { vars, putStartupRankingApplication, updateSteps } = useRankingStartup;
    const { application } = vars;
    const { steps } = application;
    const api = useApi();

    const [congresses, setCongresses] = useState<any[]>([]);
    const [caseId, setCaseId] = useState('');
    const [selectedCongress, setSelectedCongress] = useState('');
    const [cases, setCases] = useState(application?.cases || []);
    const [submitting, setSubmitting] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [saving, setSaving] = useState(false);
    const isFirstRender = useRef(true);

    // Fetch available congresses
    useEffect(() => {
        api.get('/congresses').then((res) => {
            if (Array.isArray(res)) setCongresses(res);
        }).catch(() => {});
    }, []);

    // Auto-select the only congress when there is just one option
    useEffect(() => {
        if (congresses.length === 1 && !selectedCongress) {
            setSelectedCongress(congresses[0]._id);
        }
    }, [congresses]);

    // Auto-save whenever cases change (skip first render)
    const saveCases = useCallback(async (casesToSave) => {
        setSaving(true);
        try {
            const payload = {
                ...application,
                cases: casesToSave.map((c) => ({
                    congressId: c.congressId,
                    caseId: c.caseId,
                    caseName: c.caseName || null,
                })),
            };
            if (!payload.steps) payload.steps = {};
            if (casesToSave.length > 0) payload.steps.step9 = true;

            await putStartupRankingApplication(payload);
            setLastSaved(new Date());
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }, [application, putStartupRankingApplication]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        saveCases(cases);
    }, [cases]);

    // Check for previous step conclusion
    if (!steps || steps.step8 !== true) {
        RHP.replace('/ranking/latam/startup/');
        return null;
    }

    const addCase = () => {
        if (!caseId.trim() || !selectedCongress) return;
        const alreadyExists = cases.some(
            (c) => c.caseId === caseId.trim() && c.congressId === selectedCongress
        );
        if (alreadyExists) return;

        const congress = congresses.find((c) => c._id === selectedCongress);
        setCases([
            ...cases,
            {
                congressId: selectedCongress,
                congressName: congress?.name || '',
                caseId: caseId.trim(),
            },
        ]);
        setCaseId('');
    };

    const removeCase = (index) => {
        setCases(cases.filter((_, i) => i !== index));
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        setSubmitting(true);
        try {
            await saveCases(cases);
            RHP.push('/ranking/latam/startup');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const formatSavedDate = (date: Date | null) => {
        if (!date) return '';
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    return (
        <Page.Page name="StartupStep9" back="/ranking/latam/startup" customBackText={t('glossary:backToRanking')}>
            <Page.Title>{t('StartupStep9Cases.title')}</Page.Title>
            <Page.Descr>{t('StartupStep9Cases.description')}</Page.Descr>

            {cases.length === 0 && (
                <Alert.AlertRow
                    icons="far fa-info-circle"
                    type="warning"
                    descr={t('StartupStep9Cases.emptyStateAlert')}
                />
            )}

            <form onSubmit={onSubmit}>
                <p style={{ fontWeight: 500, marginTop: 24, marginBottom: 16 }}>
                    {t('StartupStep9Cases.formInstruction')}
                </p>

                <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>
                        {t('StartupStep9Cases.caseIdLabel')}
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <input
                            type="text"
                            value={caseId}
                            onChange={(e) => setCaseId(e.target.value)}
                            placeholder={t('StartupStep9Cases.caseIdPlaceholder')}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: 4,
                            }}
                        />
                        <button
                            type="button"
                            onClick={addCase}
                            disabled={!caseId.trim() || !selectedCongress}
                            style={{
                                padding: '8px 16px',
                                background: !caseId.trim() || !selectedCongress ? '#cfd5dc' : '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: !caseId.trim() || !selectedCongress ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            + {t('StartupStep9Cases.addCase')}
                        </button>
                    </div>
                    <p style={{ fontSize: '0.85em', color: '#666', marginTop: 6, marginBottom: 0 }}>
                        {t('StartupStep9Cases.caseIdHelp')}
                    </p>

                    {congresses.length > 1 && (
                        <>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginTop: 16,
                                    marginBottom: 8,
                                }}
                            >
                                {t('StartupStep9Cases.congressLabel')}
                            </label>
                            <select
                                value={selectedCongress}
                                onChange={(e) => setSelectedCongress(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                }}
                            >
                                <option value="">{t('StartupStep9Cases.selectCongress')}</option>
                                {congresses.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>

                {cases.length > 0 && (
                    <p style={{ fontSize: '0.9em', color: '#666', marginTop: 4, marginBottom: 24 }}>
                        {t('StartupStep9Cases.hasCasesHelp')}{' '}
                        <a
                            href="https://100os.net/SubmetaSeuAbstract"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'underline' }}
                        >
                            100os.net/SubmetaSeuAbstract
                        </a>
                    </p>
                )}

                {cases.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <label style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>
                            {t('StartupStep9Cases.submittedCases')}
                        </label>
                        {cases.map((c, index) => {
                            const congress = congresses.find((cg) => cg._id === c.congressId);
                            const congressName =
                                congress?.name || c.congressName || c.congressId;
                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px 14px',
                                        border: '1px solid #eee',
                                        borderRadius: 4,
                                        marginBottom: 6,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 700 }}>
                                            {t('StartupStep9Cases.workNumberPrefix')}
                                            {c.caseId}
                                            {c.caseName && (
                                                <span style={{ fontWeight: 400 }}>
                                                    {' — '}
                                                    {c.caseName}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: '#777' }}>
                                            {congressName}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeCase(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            fontSize: 20,
                                        }}
                                        aria-label={t('glossary:delete')}
                                    >
                                        &times;
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                <Page.Ruler />

                <div style={{ marginBottom: 20 }}>
                    <p
                        style={{
                            fontWeight: 600,
                            fontSize: '1.05em',
                            color: '#444',
                            marginBottom: 8,
                        }}
                    >
                        {t('StartupStep9Cases.bonusTitle')}
                    </p>
                    <div
                        style={{ fontSize: '0.9em', color: '#555' }}
                        dangerouslySetInnerHTML={{
                            __html: t('StartupStep9Cases.bonusDescription'),
                        }}
                    />
                </div>

                <div style={{ marginBottom: 20 }}>
                    <p
                        style={{
                            fontWeight: 600,
                            fontSize: '1.05em',
                            color: '#444',
                            marginBottom: 8,
                        }}
                    >
                        {t('StartupStep9Cases.congressInfoTitle')}
                    </p>
                    <div
                        style={{ fontSize: '0.9em', color: '#555' }}
                        dangerouslySetInnerHTML={{
                            __html: t('StartupStep9Cases.congressInfoDescription'),
                        }}
                    />
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <Page.Buttn
                        style={{ width: '20%' }}
                        // @ts-ignore
                        to="/ranking/latam/startup/step8"
                    >
                        <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                        <span className="text">{t('glossary:back')}</span>
                    </Page.Buttn>

                    <Page.Buttn
                        style={{ width: '80%' }}
                        // @ts-ignore
                        to="/ranking/latam/startup"
                        onClick={() => {
                            if (steps.step8 && !steps.step9) updateSteps({ step9: true });
                        }}
                    >
                        <span className="text">{t('glossary:continue')}</span>
                        <span className="icon far fa-long-arrow-right" aria-hidden="true" />
                    </Page.Buttn>
                </div>

                {(lastSaved || saving) && (
                    <p style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 12 }}>
                        {saving
                            ? 'Salvando...'
                            : `Salvo automaticamente em ${formatSavedDate(lastSaved)}`}
                    </p>
                )}
            </form>
        </Page.Page>
    );
}
